import type { FactoryMatch, OptionValue } from "@views/Upholstery/types"
import type { SeatingRowCompareResult } from "../../pdm/compareSeatingRowToInferred"
import { compareSeatingRowToInferred } from "../../pdm/compareSeatingRowToInferred"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import {
  extractFactoryMatchAttributes,
  getApplications,
  getSeatAndRowSlices,
  getSeatingRowValue,
  isProductActive,
  normalizeString,
  normalizePartAttrs,
  shouldHideSingleRowProduct,
  unwrapValue,
  yearIncludedInApplication,
} from "../../pdm/normalizePdm"
import { FACTORY_MATCH_TRIM_KEYPAIR_KEY } from "../factory/factory-match-filter"
import { buildFactoryMatchCollapseKey } from "../steps/factory-match/aggregateFactoryMatches"
import { filterOptionsBySelectedTrim } from "../steps/trimCompatibleOptions"

/** Enabled when both `NODE_ENV=development` and `NEXT_PUBLIC_PDM_INSPECT_LOG=true`. */
export function isPdmInspectLogEnabled(): boolean {
  return (
    process.env.NODE_ENV === "development" &&
    typeof process.env.NEXT_PUBLIC_PDM_INSPECT_LOG === "string" &&
    process.env.NEXT_PUBLIC_PDM_INSPECT_LOG.trim().toLowerCase() === "true"
  )
}

export interface PdmFetchRow {
  base: string
  status: string
  trim: string
  body: string
  years: string
  usedByApp: "yes" | "no"
  dropReason: "" | "inactive" | "year" | "seating-row" | "other"
}

export interface PdmMappedTrimRow {
  trim: string
  bodyTypes: string
}

export interface PdmTrimSeatRow {
  trim: string
  passengerSeatType: string
  driverSeatType: string
}

export interface PdmTrimRowLayoutRow {
  trim: string
  frontRow: string
  middleRow: string
  backRow: string
  rowLayout: "2-row" | "3-row"
}

export interface PdmRawStructureRow {
  base_part_number: string
  population_status: string
  application_make: string
  application_model: string
  application_sub_model: string
  application_body_type: string
  application_years: string
  part_attribute_seating_row: string
  part_attribute_front_row: string
  part_attribute_middle_row: string
  part_attribute_back_row: string
  part_attribute_driver_seat_type: string
  part_attribute_passenger_seat_type: string
}

export interface PdmRowSourceRow {
  trim: string
  base_part_number: string
  source_field: "Front Row" | "Middle Row" | "Back Row"
  source_value: string
}

export interface PdmSeatingRowCheckRow extends SeatingRowCompareResult {
  trim: string
  base_part_number: string
  seatingRow: string
  middleRow: string
}

export interface PdmFactoryMatchSourceRow {
  part_number: string
  base_part_number: string
  part_number_ending: string
  trim: string
  body: string
  driver_seat: string
  passenger_seat: string
  front_row: string
  middle_row: string
  back_row: string
  primary_color: string
  insert_color: string
  stitch: string
  collapse_key: string
  drop_reason: "" | "no-part-number" | "part-number-mismatch-base"
}

export interface PdmFactoryMatchMappingRow {
  base_part_number: string
  trim: string
  body: string
  collapse_key: string
  pdm_sku_count: number
  pdm_part_numbers: string
  ui_sku: string
  collapsed_part_numbers: string
  mapping: string
  shown_in_ui: "yes" | "no" | "n/a"
}

export interface PdmFactoryMatchUiRow {
  card_id: string
  ui_sku: string
  trim_tag: string
  option_count: number
  mapped_options: string
}

function partNumberEnding(partNumber: string, basePart: string): string {
  const pn = partNumber.replace(/\s+/g, "").trim()
  const base = basePart.replace(/\s+/g, "").trim()
  if (base && pn.toUpperCase().startsWith(base.toUpperCase())) {
    return pn.slice(base.length)
  }
  return pn
}

function normalizeSkuKey(sku: string): string {
  return sku.replace(/\s+/g, "").trim().toUpperCase()
}

function applicationTrim(row: PdmFlatRow): string {
  return unwrapValue(
    row.application.sub_model ?? row.application.SubModel ?? ""
  )
}

function applicationBodyType(row: PdmFlatRow): string {
  return unwrapValue(
    row.application.body_type ??
      row.application.bodyType ??
      row.application.BodyType ??
      ""
  )
}

/**
 * Build factory-match debug tables from PDM rows.
 * Each unique part number is its own screen option (same as the UI).
 */
export function buildFactoryMatchInspectRows(
  flatRows: PdmFlatRow[],
  factoryMatches?: FactoryMatch[]
): {
  sourceRows: PdmFactoryMatchSourceRow[]
  mappingRows: PdmFactoryMatchMappingRow[]
  uiRows: PdmFactoryMatchUiRow[]
} {
  const uiSkuKeys = new Set(
    (factoryMatches ?? []).map((match) => normalizeSkuKey(match.sku))
  )

  const sourceRows: PdmFactoryMatchSourceRow[] = []
  const seenSource = new Set<string>()
  const groupedByBasePart = new Map<string, PdmFlatRow[]>()

  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    const explicitBase = unwrapValue(
      row.product.base_part_number ?? row.product.basePartNumber
    )
    const basePart = explicitBase || partNumber
    const seat = getSeatAndRowSlices(row.product)
    const attrs = extractFactoryMatchAttributes(row.product)
    const trim = applicationTrim(row)
    const body = applicationBodyType(row)

    let dropReason: PdmFactoryMatchSourceRow["drop_reason"] = ""
    if (!partNumber) {
      dropReason = "no-part-number"
    } else if (
      explicitBase &&
      !partNumber.toUpperCase().startsWith(explicitBase.toUpperCase())
    ) {
      dropReason = "part-number-mismatch-base"
    }

    const collapseKey = partNumber ? buildFactoryMatchCollapseKey(row) : ""
    const sourceKey = `${partNumber}|${trim}|${body}|${dropReason}`
    if (!seenSource.has(sourceKey)) {
      seenSource.add(sourceKey)
      sourceRows.push({
        part_number: partNumber,
        base_part_number: basePart,
        part_number_ending: partNumberEnding(partNumber, basePart),
        trim,
        body,
        driver_seat: seat.driverSeat,
        passenger_seat: seat.passengerSeat,
        front_row: seat.frontRow,
        middle_row: seat.middleRow,
        back_row: seat.backRow,
        primary_color: attrs.primaryColor,
        insert_color: attrs.insertColor,
        stitch: attrs.stitch,
        collapse_key: collapseKey,
        drop_reason: dropReason,
      })
    }

    if (dropReason) continue

    const bucket = groupedByBasePart.get(basePart) ?? []
    bucket.push(row)
    groupedByBasePart.set(basePart, bucket)
  }

  sourceRows.sort((a, b) => {
    const trimCmp = a.trim.localeCompare(b.trim)
    if (trimCmp !== 0) return trimCmp
    return a.part_number.localeCompare(b.part_number)
  })

  const mappingRows: PdmFactoryMatchMappingRow[] = []
  for (const [basePart, rows] of Array.from(groupedByBasePart.entries())) {
    const byPartNumber = new Map<string, PdmFlatRow[]>()
    for (const row of rows) {
      const partNumber = unwrapValue(
        row.product.part_number ?? row.product.PartNumber
      )
      if (!partNumber) continue
      const partRows = byPartNumber.get(partNumber) ?? []
      partRows.push(row)
      byPartNumber.set(partNumber, partRows)
    }

    for (const [partNumber, partRows] of Array.from(byPartNumber.entries())) {
      if (!partRows.length) continue
      const first = partRows[0]
      const collapseKey = first ? buildFactoryMatchCollapseKey(first) : ""
      const uiSkuKey = normalizeSkuKey(partNumber)
      mappingRows.push({
        base_part_number: basePart,
        trim: first ? applicationTrim(first) : "",
        body: first ? applicationBodyType(first) : "",
        collapse_key: collapseKey,
        pdm_sku_count: 1,
        pdm_part_numbers: partNumber,
        ui_sku: partNumber,
        collapsed_part_numbers: "",
        mapping: "Shows as its own option.",
        shown_in_ui: factoryMatches
          ? uiSkuKey && uiSkuKeys.has(uiSkuKey)
            ? "yes"
            : "no"
          : "n/a",
      })
    }
  }

  mappingRows.sort((a, b) => {
    const trimCmp = a.trim.localeCompare(b.trim)
    if (trimCmp !== 0) return trimCmp
    return a.ui_sku.localeCompare(b.ui_sku)
  })

  const uiRows: PdmFactoryMatchUiRow[] = (factoryMatches ?? []).map((card) => ({
    card_id: card.id,
    ui_sku: card.sku,
    trim_tag:
      card.keyPairs.find(
        (kp) => kp.key.toLowerCase() === FACTORY_MATCH_TRIM_KEYPAIR_KEY
      )?.values ?? "",
    option_count: card.factoryMatchOption.length,
    mapped_options: card.factoryMatchOption
      .map((opt) => `${opt.title}: ${opt.value}`)
      .join(" | "),
  }))

  return { sourceRows, mappingRows, uiRows }
}

export interface PdmFactoryMatchTrimSummaryRow {
  trim: string
  pdmSkuCount: number
  uiCardCount: number
  result: string
  keptSkus: string
  hiddenSkus: string
}

/** One row per trim: PDM SKU count vs UI card count. */
export function buildFactoryMatchTrimSummary(
  mappingRows: PdmFactoryMatchMappingRow[]
): PdmFactoryMatchTrimSummaryRow[] {
  const byTrim = new Map<string, PdmFactoryMatchMappingRow[]>()
  for (const row of mappingRows) {
    const key = row.trim || "(no trim)"
    const list = byTrim.get(key) ?? []
    list.push(row)
    byTrim.set(key, list)
  }

  return Array.from(byTrim.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([trim, rows]) => {
      const pdmSkuCount = rows.reduce((sum, r) => sum + r.pdm_sku_count, 0)
      const uiCardCount = rows.length
      const keptSkus = rows.map((r) => r.ui_sku).join(", ")
      const hiddenSkus = rows
        .map((r) => r.collapsed_part_numbers)
        .filter(Boolean)
        .join(", ")
      const result =
        pdmSkuCount === uiCardCount
          ? "Looks right. Same number in the database and on screen."
          : `Mismatch. Database has ${pdmSkuCount} part numbers. Screen shows ${uiCardCount} option${
              uiCardCount === 1 ? "" : "s"
            }.`
      return { trim, pdmSkuCount, uiCardCount, result, keptSkus, hiddenSkus }
    })
}

const TITLE_STYLE = "font-weight:bold;color:#1565c0;"
const HINT_STYLE = "color:#333;"
const WARN_STYLE = "font-weight:bold;color:#e65100;"
const ALERT_STYLE = "font-weight:bold;color:#c62828;"

function logLine(message: string, style?: string): void {
  // eslint-disable-next-line no-console
  if (style) console.log(`%c${message}`, style)
  else console.log(message)
}

function logTable(rows: unknown[]): void {
  // eslint-disable-next-line no-console
  console.table(rows)
}

function logGroup(label: string, style?: string, collapsed = false): void {
  const open = collapsed ? console.groupCollapsed : console.group
  // eslint-disable-next-line no-console
  if (style) open(`%c${label}`, style)
  else open(label)
}

function logGroupEnd(): void {
  // eslint-disable-next-line no-console
  console.groupEnd()
}

function skipReasonLabel(
  reason: PdmFactoryMatchSourceRow["drop_reason"]
): string {
  if (reason === "no-part-number") return "No part number"
  if (reason === "part-number-mismatch-base")
    return "Part number does not match its base number"
  return "No — we used this row"
}

function toFactoryMatchSourceLogRows(rows: PdmFactoryMatchSourceRow[]) {
  return rows.map((row) => ({
    "Part number from database": row.part_number,
    "Base number": row.base_part_number,
    "Ending of part number": row.part_number_ending,
    Trim: row.trim,
    Body: row.body,
    "Driver seat": row.driver_seat,
    "Passenger seat": row.passenger_seat,
    Front: row.front_row,
    Middle: row.middle_row,
    Back: row.back_row,
    "Main color": row.primary_color,
    "Insert color": row.insert_color,
    Stitch: row.stitch,
    "Did we skip this?": skipReasonLabel(row.drop_reason),
  }))
}

function toFactoryMatchMappingLogRows(rows: PdmFactoryMatchMappingRow[]) {
  return rows.map((row) => ({
    Trim: row.trim,
    Body: row.body,
    "How many part numbers in the database": row.pdm_sku_count,
    "Part numbers in the database": row.pdm_part_numbers,
    "Part number shown on screen": row.ui_sku,
    "Part number not shown as its own option":
      row.collapsed_part_numbers || "none",
    "What we did": row.mapping,
    "Shows on Factory Match?":
      row.shown_in_ui === "yes"
        ? "yes"
        : row.shown_in_ui === "no"
        ? "no"
        : "not checked yet",
  }))
}

function toFactoryMatchUiLogRows(rows: PdmFactoryMatchUiRow[]) {
  return rows.map((row) => ({
    "Part number on screen": row.ui_sku,
    "Which trims can see this option": row.trim_tag || "—",
    "How many details": row.option_count,
    "Details on this option": row.mapped_options,
  }))
}

function toFactoryMatchTrimSummaryLogRows(
  rows: PdmFactoryMatchTrimSummaryRow[]
) {
  return rows.map((row) => ({
    Trim: row.trim,
    "Part numbers in the database": row.pdmSkuCount,
    "Options on screen": row.uiCardCount,
    "Does this look right?": row.result,
    "Part number we show": row.keptSkus,
    "Part number we do not show as its own option": row.hiddenSkus || "none",
  }))
}

/**
 * Build the two tables that get printed to the console.
 * Pure function so it can be unit-tested without DOM or console.
 */
export function buildPdmInspectRows(
  products: Record<string, unknown>[],
  make: string,
  model: string,
  selectedYearNum: number,
  bodyTypeOpts: OptionValue[],
  flat?: PdmFlatRow[],
  factoryMatches?: FactoryMatch[]
): {
  rawStructureRows: PdmRawStructureRow[]
  fetchRows: PdmFetchRow[]
  mappedRows: PdmMappedTrimRow[]
  seatRows: PdmTrimSeatRow[]
  rowLayoutRows: PdmTrimRowLayoutRow[]
  rowSourceRows: PdmRowSourceRow[]
  seatingRowCheckRows: PdmSeatingRowCheckRow[]
  factoryMatchSourceRows: PdmFactoryMatchSourceRow[]
  factoryMatchMappingRows: PdmFactoryMatchMappingRow[]
  factoryMatchUiRows: PdmFactoryMatchUiRow[]
} {
  const wantMake = unwrapValue(make)
  const wantModel = unwrapValue(model)

  const rawStructureRows: PdmRawStructureRow[] = []
  const fetchRows: PdmFetchRow[] = []
  const rowSourceRows: PdmRowSourceRow[] = []
  const seatingRowCheckRows: PdmSeatingRowCheckRow[] = []

  for (const product of products) {
    const active = isProductActive(product)
    const status = unwrapValue(
      product.population_status ?? product.populationStatus ?? ""
    )
    const base = unwrapValue(
      product.base_part_number ?? product.basePartNumber ?? ""
    )
    const { frontRow, middleRow, backRow, driverSeat, passengerSeat } =
      getSeatAndRowSlices(product)
    const attrs = normalizePartAttrs(product)
    const seatingRow = getSeatingRowValue(product)

    for (const app of getApplications(product)) {
      if (!normalizeString(app.make, wantMake)) continue
      if (!normalizeString(app.model, wantModel)) continue

      const trim = unwrapValue(app.sub_model ?? app.SubModel ?? "")
      const body = unwrapValue(
        app.body_type ?? app.bodyType ?? app.BodyType ?? ""
      )
      const yearsRaw = app.years ?? app.Years
      const yearMatch = yearIncludedInApplication(yearsRaw, selectedYearNum)
      const yearsDisplay = Array.isArray(yearsRaw)
        ? yearsRaw.join(", ")
        : String(yearsRaw ?? "")

      rawStructureRows.push({
        base_part_number: base,
        population_status: status,
        application_make: unwrapValue(app.make ?? ""),
        application_model: unwrapValue(app.model ?? ""),
        application_sub_model: trim,
        application_body_type: body,
        application_years: yearsDisplay,
        part_attribute_seating_row: seatingRow,
        part_attribute_front_row: frontRow,
        part_attribute_middle_row: middleRow,
        part_attribute_back_row: backRow,
        part_attribute_driver_seat_type: driverSeat,
        part_attribute_passenger_seat_type: passengerSeat,
      })

      if (frontRow) {
        rowSourceRows.push({
          trim,
          base_part_number: base,
          source_field: "Front Row",
          source_value: frontRow,
        })
      }
      if (middleRow) {
        rowSourceRows.push({
          trim,
          base_part_number: base,
          source_field: "Middle Row",
          source_value: middleRow,
        })
      }
      if (backRow) {
        rowSourceRows.push({
          trim,
          base_part_number: base,
          source_field: "Back Row",
          source_value: backRow,
        })
      }

      seatingRowCheckRows.push({
        trim,
        base_part_number: base,
        seatingRow,
        middleRow,
        ...compareSeatingRowToInferred(seatingRow, middleRow),
      })

      let usedByApp: PdmFetchRow["usedByApp"] = "yes"
      let dropReason: PdmFetchRow["dropReason"] = ""

      if (!active) {
        usedByApp = "no"
        dropReason = "inactive"
      } else if (shouldHideSingleRowProduct(product)) {
        usedByApp = "no"
        dropReason = "seating-row"
      } else if (!yearMatch) {
        usedByApp = "no"
        dropReason = "year"
      }

      fetchRows.push({
        base,
        status,
        trim,
        body,
        years: yearsDisplay,
        usedByApp,
        dropReason,
      })
    }
  }

  const trimSet = new Set<string>()
  for (const o of bodyTypeOpts) {
    const trimCompat = o.compatibility?.find(
      (c) => c.key.toLowerCase() === "trim"
    )
    if (!trimCompat) continue
    for (const s of trimCompat.values.split(",")) {
      const t = s.trim()
      if (t) trimSet.add(t)
    }
  }
  const trims = Array.from(trimSet).sort()

  if (trims.length === 0) {
    const fallbackSet = new Set<string>()
    for (const r of fetchRows) {
      if (r.usedByApp === "yes" && r.trim) fallbackSet.add(r.trim)
    }
    for (const t of Array.from(fallbackSet).sort()) {
      trims.push(t)
    }
  }

  const mappedRows: PdmMappedTrimRow[] = trims.map((trim) => {
    const bodies = filterOptionsBySelectedTrim(bodyTypeOpts, trim).map(
      (o) => o.optionValue
    )
    return { trim, bodyTypes: bodies.join(", ") }
  })

  const seatRows: PdmTrimSeatRow[] = []
  const rowLayoutRows: PdmTrimRowLayoutRow[] = []
  if (flat && flat.length > 0) {
    const trimSeatMap = new Map<
      string,
      { passengers: Set<string>; drivers: Set<string> }
    >()
    const trimRowMap = new Map<
      string,
      { fronts: Set<string>; middles: Set<string>; backs: Set<string> }
    >()
    for (const row of flat) {
      const trim = unwrapValue(
        row.application.sub_model ?? row.application.SubModel ?? ""
      )
      if (!trim) continue
      const { passengerSeat, driverSeat, frontRow, middleRow, backRow } =
        getSeatAndRowSlices(row.product)
      if (!trimSeatMap.has(trim)) {
        trimSeatMap.set(trim, { passengers: new Set(), drivers: new Set() })
      }
      if (!trimRowMap.has(trim)) {
        trimRowMap.set(trim, {
          fronts: new Set(),
          middles: new Set(),
          backs: new Set(),
        })
      }
      const entry = trimSeatMap.get(trim)!
      const rowEntry = trimRowMap.get(trim)!
      if (passengerSeat) entry.passengers.add(passengerSeat)
      if (driverSeat) entry.drivers.add(driverSeat)
      if (frontRow) rowEntry.fronts.add(frontRow)
      if (middleRow) rowEntry.middles.add(middleRow)
      if (backRow) rowEntry.backs.add(backRow)
    }
    for (const t of Array.from(trimSeatMap.keys()).sort()) {
      const entry = trimSeatMap.get(t)!
      seatRows.push({
        trim: t,
        passengerSeatType: Array.from(entry.passengers).sort().join(", "),
        driverSeatType: Array.from(entry.drivers).sort().join(", "),
      })
    }
    for (const t of Array.from(trimRowMap.keys()).sort()) {
      const entry = trimRowMap.get(t)!
      rowLayoutRows.push({
        trim: t,
        frontRow: Array.from(entry.fronts).sort().join(", "),
        middleRow: Array.from(entry.middles).sort().join(", "),
        backRow: Array.from(entry.backs).sort().join(", "),
        rowLayout: entry.middles.size > 0 ? "3-row" : "2-row",
      })
    }
  }

  const {
    sourceRows: factoryMatchSourceRows,
    mappingRows: factoryMatchMappingRows,
    uiRows: factoryMatchUiRows,
  } = flat && flat.length > 0
    ? buildFactoryMatchInspectRows(flat, factoryMatches)
    : { sourceRows: [], mappingRows: [], uiRows: [] }

  return {
    rawStructureRows,
    fetchRows,
    mappedRows,
    seatRows,
    rowLayoutRows,
    rowSourceRows,
    seatingRowCheckRows,
    factoryMatchSourceRows,
    factoryMatchMappingRows,
    factoryMatchUiRows,
  }
}

/**
 * Print a formatted PDM inspect log to the browser console.
 * Only runs when `isPdmInspectLogEnabled()` is true.
 */
export function logPdmVehicleInspect(
  products: Record<string, unknown>[],
  make: string,
  model: string,
  selectedYearNum: number,
  flat: PdmFlatRow[],
  bodyTypeOpts: OptionValue[],
  factoryMatches?: FactoryMatch[]
): void {
  if (!isPdmInspectLogEnabled()) return

  const {
    rawStructureRows,
    fetchRows,
    mappedRows,
    seatRows,
    rowLayoutRows,
    rowSourceRows,
    seatingRowCheckRows,
    factoryMatchSourceRows,
    factoryMatchMappingRows,
    factoryMatchUiRows,
  } = buildPdmInspectRows(
    products,
    make,
    model,
    selectedYearNum,
    bodyTypeOpts,
    flat,
    factoryMatches
  )

  const label = `${make} ${model} ${selectedYearNum}`
  const bannerStyle =
    "background:#1565c0;color:#fff;font-size:13px;font-weight:bold;padding:4px 8px;border-radius:4px;"

  logGroup(`[PDM Inspect] ${label}`, bannerStyle)
  logLine(
    `This log appears when you pick a car. The database sent ${products.length} products. We used ${flat.length} rows for this car.`
  )
  logLine(
    "Open section 2 first. That is Factory Match. Section 1 is extra detail and starts closed."
  )

  logGroup("1. Body, seats, and rows (extra detail)", TITLE_STYLE, true)
  logLine(
    "These tables show body type, seats, and rows. You can skip this if you only care about Factory Match.",
    HINT_STYLE
  )

  logLine("Raw fields from the database", TITLE_STYLE)
  logTable(rawStructureRows)

  logLine("Rows for this make and model", TITLE_STYLE)
  logTable(fetchRows)

  logLine("Which body types each trim can use", TITLE_STYLE)
  logTable(mappedRows)

  if (seatRows.length > 0) {
    logLine("Which seat types each trim can use", TITLE_STYLE)
    logTable(seatRows)
  }

  if (rowLayoutRows.length > 0) {
    logLine("Which row layouts each trim can use", TITLE_STYLE)
    logTable(rowLayoutRows)
  }

  if (rowSourceRows.length > 0) {
    logLine("Where each row choice came from in the database", TITLE_STYLE)
    logTable(rowSourceRows)
  }

  if (seatingRowCheckRows.length > 0) {
    logLine(
      "Does “2 row / 3 row” in the database match the middle row?",
      TITLE_STYLE
    )
    logTable(seatingRowCheckRows)

    const mismatches = seatingRowCheckRows.filter((r) => r.match === "no")
    if (mismatches.length > 0) {
      logLine(
        `These ${mismatches.length} rows do not match. The database seating-row field and the middle-row field disagree.`,
        ALERT_STYLE
      )
      logTable(mismatches)
    }
  }

  const dropped = fetchRows.filter((r) => r.usedByApp === "no")
  if (dropped.length > 0) {
    logLine("We did not use these rows on screen", WARN_STYLE)
    logTable(dropped)
  }
  logGroupEnd()

  if (factoryMatchSourceRows.length > 0) {
    const pdmSkuCount = new Set(
      factoryMatchSourceRows
        .filter((r) => !r.drop_reason && r.part_number)
        .map((r) => r.part_number)
    ).size
    const hasUiSnapshot = factoryMatchMappingRows.some(
      (r) => r.shown_in_ui !== "n/a"
    )
    const uiCardCount = hasUiSnapshot
      ? factoryMatchUiRows.length
      : factoryMatchMappingRows.length
    const collapsedGroups = factoryMatchMappingRows.filter(
      (r) => r.pdm_sku_count > 1
    )
    const trimSummary = buildFactoryMatchTrimSummary(factoryMatchMappingRows)

    logGroup(
      "2. Factory Match — database vs what you see on screen",
      TITLE_STYLE
    )
    logLine(
      `The database has ${pdmSkuCount} factory match part number(s). The screen has ${uiCardCount} option(s).`
    )
    logLine(
      "How to check: look at table A. Find the trim you picked (for example Si). The database count and the screen count should match.",
      HINT_STYLE
    )
    if (collapsedGroups.length > 0) {
      logLine(
        "Some part numbers were combined. In table A, look for a mismatch in the last column.",
        WARN_STYLE
      )
    }

    if (trimSummary.length > 0) {
      logLine("A. Quick check — one line per trim", TITLE_STYLE)
      logLine(
        "This is the table to start with. Compare “part numbers in the database” with “options on screen”.",
        HINT_STYLE
      )
      logTable(toFactoryMatchTrimSummaryLogRows(trimSummary))
    }

    logLine("B. What the database sent", TITLE_STYLE)
    logLine(
      "Each row is one factory match part number. Each part number is its own option on screen.",
      HINT_STYLE
    )
    logTable(toFactoryMatchSourceLogRows(factoryMatchSourceRows))

    if (factoryMatchMappingRows.length > 0) {
      logLine(
        "C. How we turn database part numbers into screen options",
        TITLE_STYLE
      )
      logLine(
        "Each database part number should show as its own option on screen.",
        HINT_STYLE
      )
      logTable(toFactoryMatchMappingLogRows(factoryMatchMappingRows))

      if (collapsedGroups.length > 0) {
        logLine(
          `These ${collapsedGroups.length} group(s) still list more than one part number on one mapping row.`,
          ALERT_STYLE
        )
        logTable(toFactoryMatchMappingLogRows(collapsedGroups))
      }
    }

    if (factoryMatchUiRows.length > 0) {
      logLine("D. What the Factory Match step shows", TITLE_STYLE)
      logLine(
        "Match the part number and the details here with the option on the page. “Which trims can see this option” is who this option is for.",
        HINT_STYLE
      )
      logTable(toFactoryMatchUiLogRows(factoryMatchUiRows))
    }
    logGroupEnd()
  }

  logGroupEnd()
}
